import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, readLastUser, readLastPhoto } from '../lib/auth.jsx';
import { get } from '../lib/api.js';
import { Avatar, ErrorNote } from '../components/ui.jsx';
import { Icon } from '../lib/icons.jsx';
import { LOGO } from '../lib/logo.js';
import { DEMO_ACCOUNTS } from '../lib/nav.js';
import { ALL_QUOTES } from '../lib/quotes.js';

/* ------------------------------------------------------------------- captcha */

const digits = () => String(Math.floor(Math.random() * 90) + 10); // always two digits

// Only ever read behind an import.meta.env.DEV guard, so it is stripped from
// production bundles along with the panel that shows it.
const DEMO_PASSWORD = 'Password@123';

/**
 * Draws the challenge as SVG glyphs rather than text.
 *
 * Each digit gets its own rotation, offset and weight, over a few noise strokes.
 * The point is that the value is not sitting in the DOM as selectable text that
 * a script could read straight off the page — which is the whole reason to draw
 * it at all rather than print it.
 */
function CaptchaImage({ value }) {
  // Regenerated with the value so the distortion changes on every refresh.
  const glyphs = useMemo(() => value.split('').map((ch, i) => ({
    ch,
    x: 30 + i * 44,
    y: 40 + (Math.random() * 8 - 4),
    rotate: Math.random() * 36 - 18,
    size: 30 + Math.random() * 6,
    weight: Math.random() > 0.5 ? 800 : 650,
  })), [value]);

  const noise = useMemo(() => Array.from({ length: 4 }, () => ({
    x1: Math.random() * 104, y1: Math.random() * 46,
    x2: Math.random() * 104, y2: Math.random() * 46,
  })), [value]);

  const dots = useMemo(() => Array.from({ length: 14 }, () => ({
    cx: Math.random() * 104, cy: Math.random() * 46, r: Math.random() * 1.3 + 0.4,
  })), [value]);

  return (
    <svg viewBox="0 0 104 46" role="img" aria-label="Verification image">
      {noise.map((n, i) => (
        <line key={`n${i}`} {...n} stroke="var(--border-strong)" strokeWidth="1" opacity=".7" />
      ))}
      {dots.map((d, i) => <circle key={`d${i}`} {...d} fill="var(--text-3)" opacity=".45" />)}
      {glyphs.map((g, i) => (
        <text
          key={`g${i}`}
          x={g.x}
          y={g.y}
          fontSize={g.size}
          fontWeight={g.weight}
          fill="var(--text)"
          fontFamily="var(--ffm)"
          transform={`rotate(${g.rotate} ${g.x} ${g.y})`}
        >
          {g.ch}
        </text>
      ))}
    </svg>
  );
}

/* --------------------------------------------------------------------- page */

export default function Login() {
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Convenience only while developing. A production build ships empty fields and
  // no credential hints — the deployed site is public, and a pre-filled working
  // login is an open door.
  const lastUser = useMemo(() => readLastUser(), []);
  const lastPhoto = useMemo(() => readLastPhoto(), []);
  const [email, setEmail] = useState(
    import.meta.env.DEV ? 'hr@sevenmentor.com' : (lastUser?.email ?? ''),
  );
  const [password, setPassword] = useState(import.meta.env.DEV ? DEMO_PASSWORD : '');
  const [captcha, setCaptcha] = useState(digits);
  const [answer, setAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Rotating quote. A ref keeps the timer from resetting on every render.
  const [index, setIndex] = useState(() => Math.floor(Math.random() * ALL_QUOTES.length));
  const paused = useRef(false);
  const googleButton = useRef(null);

  /* What this server actually offers. Asked rather than assumed, so an
     installation with no Google client configured draws no Google button and
     one that has it needs no rebuild to show it. */
  const [providers, setProviders] = useState(null);
  useEffect(() => {
    let alive = true;
    get('/auth/providers')
      .then((r) => { if (alive) setProviders(r.data); })
      .catch(() => { if (alive) setProviders({ password: true, google: false }); });
    return () => { alive = false; };
  }, []);

  /* Google's script is loaded only if it is going to be used — a sign-in page
     that reaches out to a third party before knowing whether it needs to is a
     request every visitor pays for, including the ones who never click it. */
  useEffect(() => {
    if (!providers?.google || !googleButton.current) return undefined;
    let cancelled = false;

    const render = () => {
      if (cancelled || !window.google?.accounts?.id || !googleButton.current) return;
      window.google.accounts.id.initialize({
        client_id: providers.googleClientId,
        callback: async ({ credential }) => {
          setBusy(true);
          setError(null);
          try {
            await signInWithGoogle(credential);
            navigate('/', { replace: true });
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        },
        // Which accounts Google offers. The server checks the domain again on
        // the token it is given; this only saves somebody picking an account
        // that was always going to be refused.
        hd: providers.googleDomain,
      });
      googleButton.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButton.current, {
        theme: 'outline', size: 'large', width: 320, text: 'signin_with',
      });
    };

    const existing = document.querySelector('script[data-google-identity]');
    if (existing) { render(); return () => { cancelled = true; }; }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.dataset.googleIdentity = 'true';
    script.onload = render;
    script.onerror = () => {
      if (!cancelled) setError(new Error('Google sign-in could not load. Use your password.'));
    };
    document.head.appendChild(script);
    return () => { cancelled = true; };
  }, [providers, signInWithGoogle, navigate]);

  const nextQuote = useCallback(() => setIndex((i) => (i + 1) % ALL_QUOTES.length), []);

  useEffect(() => {
    const t = setInterval(() => { if (!paused.current) nextQuote(); }, 9000);
    return () => clearInterval(t);
  }, [nextQuote]);

  /** New challenge and an empty box. Deliberately does not touch the error —
   *  clearing it here would swap the image out with nothing explaining why. */
  const newChallenge = () => {
    setCaptcha(digits());
    setAnswer('');
  };

  /** The refresh button, where clearing the message is the right thing to do. */
  const refreshCaptcha = () => {
    newChallenge();
    setCaptchaError(null);
  };

  async function submit(event) {
    event.preventDefault();
    setError(null);
    setCaptchaError(null);

    if (answer.trim() !== captcha) {
      newChallenge();
      setCaptchaError('That does not match the image. A new one has been generated.');
      return;
    }

    setBusy(true);
    try {
      await signIn(email.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err);
      // A fresh challenge after a failure, so one solved answer cannot be
      // replayed across repeated password attempts.
      newChallenge();
    } finally {
      setBusy(false);
    }
  }

  const quote = ALL_QUOTES[index];
  // Eight markers standing in for the whole set — a dot per quote would be absurd.
  const markers = 8;

  return (
    <div className="login">
      <div
        className="login-art"
        onMouseEnter={() => { paused.current = true; }}
        onMouseLeave={() => { paused.current = false; }}
      >
        <div className="login-brand">
          <div className="brand-mark"><img src={LOGO} alt="" /></div>
          <div>
            <div className="login-brand-name">SevenMentor</div>
            <div className="login-brand-sub">Smarter HR. Stronger Teams.</div>
          </div>
        </div>

        <div className="quote-block">
          <div className="quote-wrap" aria-hidden="true">
            <div className="quote-mark">&ldquo;</div>
            {/* Keyed so React remounts and replays the entrance on every change. */}
            {quote.hi && <p className="quote-hi" key={`hi-${index}`} lang="hi">{quote.hi}</p>}
            <p className={`quote-en${quote.hi ? '' : ' quote-lead'}`} key={`en-${index}`}>{quote.en}</p>
            {quote.by && <p className="quote-by" key={`by-${index}`}>— {quote.by}</p>}
          </div>

          <div className="quote-foot">
            <div className="quote-dots" aria-hidden="true">
              {Array.from({ length: markers }, (_, i) => (
                <i key={i} className={i === index % markers ? 'on' : ''} />
              ))}
            </div>
            <button type="button" className="quote-next" onClick={nextQuote} aria-label="Next quote">
              <Icon name="chevR" />
            </button>
          </div>
        </div>

        <div className="login-facts">
          <span><Icon name="fingerprint" /> Biometric attendance</span>
          <span><Icon name="shield" /> Role &amp; record scoping</span>
          <span><Icon name="history" /> Append-only audit trail</span>
        </div>
      </div>

      <div className="login-form">
        <div className="login-box">
          {/* Shown only where the art panel is collapsed, so somebody signing in
              on a phone still gets the thought for the day. */}
          <button type="button" className="login-quote-mobile" onClick={nextQuote}>
            {quote.hi && <span className="quote-hi" lang="hi" key={`m-${index}`}>{quote.hi}</span>}
            <span className="quote-en">{quote.en}</span>
            {quote.by && <span className="quote-by">— {quote.by}</span>}
          </button>

          <div style={{ marginBottom: 20 }}>
            {lastUser && (
              <div className="login-face">
                <Avatar
                  name={lastUser.fullName}
                  size="lg"
                  src={lastPhoto}
                  hasPhoto={Boolean(lastPhoto)}
                />
              </div>
            )}
            <div style={{ fontWeight: 750, fontSize: 22, letterSpacing: '-.035em' }}>
              {lastUser?.firstName ? `Welcome back, ${lastUser.firstName}` : 'Welcome back'}
            </div>
            <div className="txt-sm muted">Sign in with your SevenMentor work email</div>
          </div>

          <div className="card">
            <div className="card-b">
              <form onSubmit={submit}>
                <div className="field">
                  <label htmlFor="login-email">Work email</label>
                  <input
                    id="login-email" className="inp" type="email" value={email} autoComplete="username"
                    onChange={(e) => setEmail(e.target.value)} required
                  />
                </div>

                <div className="field">
                  <label htmlFor="login-password">Password</label>
                  <div className="password-wrap">
                    <input
                      id="login-password" className="inp" type={showPassword ? 'text' : 'password'}
                      value={password} autoComplete="current-password"
                      onChange={(e) => setPassword(e.target.value)} required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                    >
                      <Icon name={showPassword ? 'eyeOff' : 'eye'} />
                    </button>
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="login-captcha">Type the two digits shown</label>
                  <div className="captcha-row">
                    <div className="captcha-box"><CaptchaImage value={captcha} /></div>
                    <input
                      id="login-captcha"
                      className="inp"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={2}
                      placeholder="00"
                      value={answer}
                      aria-describedby={captchaError ? 'captcha-error' : undefined}
                      onChange={(e) => setAnswer(e.target.value.replace(/\D/g, ''))}
                      required
                    />
                    <button type="button" className="captcha-refresh" onClick={refreshCaptcha} aria-label="New image">
                      <Icon name="refresh" />
                    </button>
                  </div>
                  {captchaError && (
                    <div className="hint" id="captcha-error" style={{ color: 'var(--crit)' }}>{captchaError}</div>
                  )}
                </div>

                <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={busy}>
                  {busy ? 'Signing in…' : <><Icon name="logIn" />Sign in</>}
                </button>
              </form>

              {/* Only when the server says it is configured. A button that
                  cannot work is worse than no button: somebody tries it,
                  it fails, and they doubt their password too. */}
              {providers?.google && (
                <>
                  <div className="flex items-c gap-10" style={{ margin: '16px 0 12px' }}>
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                    <span className="txt-sm muted">or</span>
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  </div>
                  <div ref={googleButton} style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }} />
                  <div className="txt-sm muted" style={{ marginTop: 8, textAlign: 'center' }}>
                    Your <b>@{providers.googleDomain}</b> account only.
                  </div>
                </>
              )}

              <div className="txt-sm muted" style={{ marginTop: 14, textAlign: 'center' }}>
                <Link to="/reset-password">Forgotten your password?</Link>
              </div>

              <ErrorNote error={error} />
            </div>
          </div>

          {import.meta.env.DEV && (
          <div className="card mt-14">
            <div className="card-b">
              <div style={{ fontWeight: 650, fontSize: 13.4, marginBottom: 4 }}>Seeded demo accounts</div>
              <div className="txt-sm muted" style={{ marginBottom: 10 }}>
                Password for all four is <span className="mono">{DEMO_PASSWORD}</span>
              </div>
              {DEMO_ACCOUNTS.map((account) => (
                <button
                  key={account.email}
                  type="button"
                  className="check-item"
                  style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }}
                  onClick={() => { setEmail(account.email); setPassword(DEMO_PASSWORD); }}
                >
                  <div style={{ flex: 1 }}>
                    <div className="ct">{account.label}</div>
                    <div className="cs">{account.email} · {account.note}</div>
                  </div>
                </button>
              ))}
              <div className="txt-sm muted mt-8">
                Each has a different saved theme, so switching accounts shows the appearance system working.
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
